/******************************************
 *  Author : vsuthar
 *  Created On : Wed Sep 01 2021
 *  File : GlobalConstants.js
 *  ©Palo Alto Networks
 *******************************************/

import type { Tsg } from '@sparky/framework'
import { AVAILABLE_IN_TENANT } from '../../subscription/constants/LabelConstants'

/***
 * ================================
 *             CAUTION
 * ================================
 * ONLY GLOBAL CONSTANTS SHOULD GOES HERE WHICH ARE SHARING WITHIN ALL MODULES EXAMPLE IAM, SUBSCRIPTION, TEANTNCY
 * ALL OTHER INDIVIDUAL LEVEL CONSTANTS GOES TO RESPECTED MODULES
 */

export const MICRO_APP_NAME = 'cloud-mgmt'
export const ASC_PORTAL_MICRO_APP = 'asc-partner-app'
export const MSP_MICRO_APP = 'sase-msp'

export const SPARKY_ACCESS_READ = 'read'
export const SPARKY_ACCESS_NONE = 'no_access'
export const SPARKY_ACCESS_WRITE = 'write'
export const SUBSCRIPTION_PERMISSION_SET = 'common_services.subscriptions'
export const TENANCY_PERMISSION_SET = 'common_services.tenancy'
export const IAM_PERMISSION_SET = 'common_services.iam'
export const DEVICES_PERMISSION_SET = 'common_services.devices'
export const IP_ALLOW_PERMISSION_SET = 'common_services.ip_restriction_policy'

export const ASC_TIER_1_SUPPORT_ROLE = 'tier_1_support'
export const BROWSER_ROLE = 'browser'

export const VIEW_TENANT_SWITCHER = '/tenant_switcher'
export const VIEW_MANAGE_TENANT = '/tenant_management'
export const VIEW_IAM_TENANT = '/iam_tenant'
export const VIEW_MANAGE_IAM = '/iam_management'
export const VIEW_HUB = '/hub'
export const ASC_URL = '/asc'
export const MSP_URL = '/msp'

//export const VIEW_MANAGE_SUBSCRIPTION_LINK = '/subscription_management'
export const VIEW_IAM_ROLES = '/iam_roles'
export const VIEW_IAM_PROVIDERS = '/iam_idp'
export const VIEW_ADD_CUSTOM_ROLE = '/iam_add_role'
export const VIEW_EDIT_CUSTOM_ROLE = '/iam_edit_role'
export const VIEW_IAM_IDENTITY_FEDERATIONS = '/iam_identity_federations'

export const VIEW_MANAGE_SUBSCRIPTION = '/subscription'
export const VIEW_DEVICE_ASSOCIATIONS = '/devices'

export const VIEW_IP_ALLOW_LIST = '/ip_allow_list'

export const COMMON_SERVICES_URL_PREFIX = '/settings'
export const VIEW_PAGE_PATH_MAPPING = {
  [VIEW_MANAGE_TENANT]: `${COMMON_SERVICES_URL_PREFIX}/tenants`,
  [VIEW_MANAGE_SUBSCRIPTION]: `${COMMON_SERVICES_URL_PREFIX}/subscription`,
  [VIEW_DEVICE_ASSOCIATIONS]: `${COMMON_SERVICES_URL_PREFIX}/devices`,
  [VIEW_MANAGE_IAM]: `${COMMON_SERVICES_URL_PREFIX}/iam/access`,
  [VIEW_IAM_ROLES]: `${COMMON_SERVICES_URL_PREFIX}/iam/roles`,
  [VIEW_IAM_IDENTITY_FEDERATIONS]: `${COMMON_SERVICES_URL_PREFIX}/iam/identities`,
  [VIEW_ADD_CUSTOM_ROLE]: `${COMMON_SERVICES_URL_PREFIX}/iam/roles/add`,
  [VIEW_EDIT_CUSTOM_ROLE]: `${COMMON_SERVICES_URL_PREFIX}/iam/roles/edit`,
} as Record<string, string | undefined>

export const APP_ID_LOGGING_SERVICE = 'logging_service'
export const APP_ID_PRISMA_ACCESS = 'prisma_access'
export const APP_ID_DSS_ID = 'directory_sync'
export const APP_ID_PRISMA_SAAS = 'aperture'
export const APP_ID_PRISMA_ACCESS_PANORAMA = 'prisma_access_panorama'
export const APP_ID_PRISMA_ACCESS_EDITION = 'prisma_access_edition'
export const APP_ID_PRISMA_ACCESS_EDITION_ONPREM = `${APP_ID_PRISMA_ACCESS_EDITION}_onprem`
export const APP_ID_PRISMA_ACCESS_MOBILE_USERS = 'prisma_access_for_mobile_users'
export const APP_ID_PRISMA_ACCESS_REMOTE_NETWORKS = 'prisma_access_for_remote_networks'
export const APP_ID_PRISMA_ACCESS_FOR_CLEAN_PIPE = 'prisma_access_for_clean_pipe'
export const APP_ID_AUTONOMOUS_DEM = 'adem'
export const APP_ID_AUTONOMOUS_DEM_MOBILE = 'adem_for_mobile'
export const APP_ID_AUTONOMOUS_DEM_REMOTE_NETWORKS = 'adem_for_remote_networks'
export const APP_ID_NET_INTER_CONNECT = 'net_interconnect'
export const APP_ID_PRIVATE_APPS = 'private_app_access'
export const APP_ID_PRIVATE_APPS_QTY = 'private_app_access_qty'
export const APP_ID_DATA_TRANSFER = 'data_transfer'
export const APP_ID_DLP = 'dlp'
export const APP_ID_IOT = 'zingbox'
export const APP_ID_CUSTOMER_SUCCESS = 'customer_success'
export const APP_ID_PRISMA_ACCESS_ONPERM = 'prisma_access_edition_onprem'
export const APP_ID_SERVICE_CONNECTION = 'service_connection'
export const APP_ID_SDWAN = 'cgx'
export const APP_ID_DVR = 'dvr'
export const APP_ID_PANORAMA = 'panorama'
export const APP_ID_OKYO = 'OKYO'
export const APP_ID_SUPPORT = 'support'
export const APP_ID_CASB = 'CASB'
export const APP_ID_CASB_NGFW = 'CASB_NGFW'
export const CTX_CDL = 'cdl'
export const APP_ID_SERVICE_LOC = 'add_loc'
export const APP_ID_CIE = APP_ID_DSS_ID
export const APP_ID_PAN_CHINA = 'pan_china'
export const APP_ID_AI_OPS = 'add_aiops'
export const APP_ID_NG_CASB = 'ng_casb'
export const APP_ID_FEDRAMP = 'fedramp'
export const NGFW = 'ngfw'
export const PANORAMA = 'panorama'
export const VM_FLEX = 'vmflex'
export const VM_PANORAMA = 'vm-panorama'
export const APP_ID_ADEM_AIOPS = 'add_adem_aiops'
export const APP_ID_EMAIL_DLP = 'add_email_dlp'
export const APP_ID_STRATA_INSIGHTS = 'strata_insights'
export const APP_ID_STRATA_INSIGHTS_FREE = 'strata_insights_free'
export const APP_ID_ZTNA = 'add_ztna'
export const APP_ID_BACKBONE = 'mssp_backbone'
export const APP_ID_COLO_CONNECT = 'add_colo_connect'
export const APP_ID_TRAFFIC_MIRRORING = 'add_traffic_mirroring'
export const APP_ID_SCM = 'strata_cloud_manager'
export const APP_ID_APP_ACC = 'add_app_accl'

export const GOV_REGION = 'gov'

export const SHARED_INSTANCE = 'Shared Instance'

export const PA_DEFAULT_ROUTE = '/prisma-access'

export const LICENSE_TYPE_FED_MOD = 'FEDRAMP-MOD'
export const LICENSE_TYPE_FED_HIGH = 'FEDRAMP-HIGH'

export const MULTIPLE_APPID = [APP_ID_PRISMA_ACCESS_EDITION, APP_ID_AUTONOMOUS_DEM, APP_ID_PRISMA_ACCESS_ONPERM, APP_ID_ADEM_AIOPS]
export const HUB_LANDING_PATH = '/hub'
export const ALLOWED_LICENSE_CONTEXTS = [APP_ID_SDWAN, CTX_CDL, APP_ID_NG_CASB]
/**
 * This application will not render in Tenant Switcher and Tenant Management
 * @type {(string|string)[]}
 */
export const DONT_RENDER_APP = [APP_ID_PRISMA_ACCESS_EDITION, APP_ID_PRISMA_ACCESS_EDITION_ONPREM]

export const LICENSE_CONTEXT_TO_APP_ID = {
  [CTX_CDL]: APP_ID_LOGGING_SERVICE,
  [APP_ID_SDWAN]: APP_ID_SDWAN,
  [APP_ID_PRISMA_ACCESS]: APP_ID_PRISMA_ACCESS_EDITION,
  [APP_ID_NG_CASB]: APP_ID_NG_CASB
}

export const APP_ID_FOR_TITLE = {
  [CTX_CDL]: APP_ID_LOGGING_SERVICE,
  [APP_ID_SDWAN]: APP_ID_PRISMA_ACCESS,
  [APP_ID_PRISMA_ACCESS]: APP_ID_PRISMA_ACCESS,
  [APP_ID_NG_CASB]: APP_ID_NG_CASB,
  [APP_ID_LOGGING_SERVICE]: APP_ID_LOGGING_SERVICE
}
export const TSG_ERRORS = {
  [CTX_CDL]: 'Selected Tenant already has a CDL instance',
  [APP_ID_SDWAN]: 'Selected Tenant already has an SDWAN instance',
  [APP_ID_NG_CASB]: 'Selected Tenant already has NG-CASB instance'
}
export const CLOUD_APPS = new Set([APP_ID_SDWAN, CTX_CDL, APP_ID_NG_CASB])
export const ENTERPRISE_FLOW_APPS = new Set([CTX_CDL, APP_ID_NG_CASB])


export const BANNER_MESSAGES = {
  [APP_ID_NG_CASB]: { //Based on the licenseContext
    'info': 'When CASB-X activation is finished, please choose firewalls in the Device Associations tab.',
  }
}

export const NON_PANORAMA_APPS = new Set([APP_ID_NG_CASB]) // These apps will hide Panorama specific cards from the onboarding flow
// export const CONNECTED_PLATFORMS = {
//   [APP_ID_NG_CASB]: new Set([APP_ID_PRISMA_ACCESS])
// }
export const CONNECTED_PLATFORMS = {
  [APP_ID_NG_CASB]: new Map(
    [
      [APP_ID_PRISMA_ACCESS, {
        platform: 'Prisma Access',
        tenantStatus: AVAILABLE_IN_TENANT,
        message: 'To use CASB capabilities, you must be on the Prisma Access 3.2.x Innovation version or newer. Contact your Palo Alto Networks account representative if CASB features don\'t show up after the activation.'
      }],
      [APP_ID_CASB_NGFW, {
        platform: 'NGFW',
        tenantStatus: 'If available in this tenant'
      }
      ]
    ]
  )
}

export const TSG_NONE = {
  id: 'none',
  display_name: '(No Tenant)',
  isNone: true,
} as Tsg

export const LG_BASE_FONT_SIZE = '16px'

export const ACTIVATION_TYPE = {
  AMEND: 'AMEND',
  NEW: 'NEW'
}

export const ELA = 'ELA' // this is subscription type not license type

export const ERROR_API_IP_RESTRICTED = 'API Access is IP restricted.'
export const ERROR_API_IP_RESTRICTED_TSG = 'Current IP address has access restrictions for this tenant.'

export const DAS_FILTER_DEVICE_TYPES = [NGFW, VM_FLEX, VM_PANORAMA, PANORAMA] // allowed types from UI
